import { CampusConfig } from '../types/config';

// Function to parse time strings like "08:00AM" or "08:50AM" into minutes from midnight
const parseTime = (timeStr: string): number => {
  const [time, modifier] = timeStr.split(/(AM|PM)/);
  let [hours, minutes] = time.split(':').map(Number);

  if (modifier === 'PM' && hours < 12) {
    hours += 12;
  }
  if (modifier === 'AM' && hours === 12) {
    hours = 0;
  }
  return hours * 60 + minutes;
};

// Function to parse "HH:MM(AM|PM)\nto\nHH:MM(AM|PM)" into a time range
const parseTimeRange = (timeRangeStr: string): { start: number; end: number } | null => {
  if (!timeRangeStr || !timeRangeStr.includes('to')) return null;
  const [startStr, endStr] = timeRangeStr.replace(/\n/g, '').split('to');
  return {
    start: parseTime(startStr.trim()),
    end: parseTime(endStr.trim()),
  };
};

interface SlotInfo {
  day: string;
  start: number;
  end: number;
}

type SlotTimings = Map<string, SlotInfo>;

export const generateSlotTimings = (config: CampusConfig): SlotTimings => {
  const timings: SlotTimings = new Map();
  const { dayRows, theoryTimes, labTimes } = config;

  dayRows.forEach(({ day, slots }) => {
    let theoryTimeIndex = 0;
    let labTimeIndex = 0;

    slots.forEach(slotItem => {
      if (typeof slotItem === 'string') {
        const slotNames = slotItem.split('/');
        
        // Handle both theory and lab slots that can appear in the same cell
        slotNames.forEach(slotName => {
          if (slotName.startsWith('L')) {
            // Lab slot
            const timeRange = parseTimeRange(labTimes[labTimeIndex]);
            if (timeRange) {
              timings.set(slotName, { day, ...timeRange });
            }
          } else {
            // Theory slot
            const timeRange = parseTimeRange(theoryTimes[theoryTimeIndex]);
            if (timeRange) {
              timings.set(slotName, { day, ...timeRange });
            }
          }
        });

        // Only increment indices once per cell
        if (slotNames[0].startsWith('L')) {
          labTimeIndex++;
          theoryTimeIndex++; // some theory slots are empty
        } else {
          theoryTimeIndex++;
          labTimeIndex++;
        }
      } else {
        // Handle colspan items (e.g., Extramural Hours)
        theoryTimeIndex += slotItem.colspan || 1;
        labTimeIndex += slotItem.colspan || 1;
      }
    });
  });

  return timings;
};

export const hasTimeOverlap = (timing1: SlotInfo, timing2: SlotInfo): boolean => {
  if (timing1.day !== timing2.day) return false;
  return timing1.start < timing2.end && timing1.end > timing2.start;
};

export const generateTimeBasedConflicts = (config: CampusConfig): string[][] => {
  const slotTimings = generateSlotTimings(config);
  const slots = Array.from(slotTimings.keys());
  const conflicts: string[][] = [];
  const checkedPairs = new Set<string>();

  // First, filter out excluded slots
  const validSlots = slots.filter(slot => !config.EXCLUDED_SLOTS.includes(slot));

  for (let i = 0; i < validSlots.length; i++) {
    for (let j = i + 1; j < validSlots.length; j++) {
      const slot1 = validSlots[i];
      const slot2 = validSlots[j];

      const pairKey = `${slot1}-${slot2}`;
      const reversePairKey = `${slot2}-${slot1}`;

      if (checkedPairs.has(pairKey) || checkedPairs.has(reversePairKey)) {
        continue;
      }

      const timing1 = slotTimings.get(slot1);
      const timing2 = slotTimings.get(slot2);

      if (timing1 && timing2 && hasTimeOverlap(timing1, timing2)) {
        conflicts.push([slot1, slot2]);
      }

      checkedPairs.add(pairKey);
    }
  }

  return conflicts;
};

// Helper function to check if a slot conflicts with any slots in a list
export const hasConflictWithSlots = (
  slot: string,
  existingSlots: string[],
  config: CampusConfig
): boolean => {
  const slotTimings = generateSlotTimings(config);
  const timing = slotTimings.get(slot);

  if (!timing || config.EXCLUDED_SLOTS.includes(slot)) return false;

  return existingSlots.some(existingSlot => {
    const existingTiming = slotTimings.get(existingSlot);
    return existingTiming && hasTimeOverlap(timing, existingTiming);
  });
}; 